import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Trash2, Plus, Info, ChevronDown, X } from "lucide-react";
import { WaterEntry, PollutantEntry, PollutantDocWithId, DischargeEntry } from "@/types/water";
import { useState, useRef, useEffect } from "react";
import { normalizeText } from "@/utils/textFormatter";

interface WaterDischargeTabProps {
  formEntry: WaterEntry;
  onFormEntryChange: (entry: WaterEntry) => void;
  dischargeTypes: string[];
  pollutants: PollutantDocWithId[];
  errors: Record<string, string>;
  touched: Record<string, boolean>;
  onFieldChange: (field: string, value: any) => void;
  onFieldBlur: (field: string) => void;
}

export const WaterDischargeTab = ({
  formEntry,
  onFormEntryChange,
  dischargeTypes,
  pollutants,
  errors,
  touched,
  onFieldChange,
  onFieldBlur,
}: WaterDischargeTabProps) => {
  const { t } = useLanguage();
  
  // State for custom dropdowns
  const [openDropdowns, setOpenDropdowns] = useState<Record<number, boolean>>({});
  const [searchValues, setSearchValues] = useState<Record<number, string>>({});
  const [isScrolling, setIsScrolling] = useState(false);
  const dropdownRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Empty pollutant entry
  const emptyPollutant: PollutantEntry = {
    pollutantId: "",
    pollutantName: "",
    concentration: "",
    hasMonitoring: null, // No pre-selection
    concentrationInReceiver: "",
  };

  // Discharge handlers
  const handleDischargeTypeToggle = (dischargeType: string) => {
    const existingIndex = formEntry.discharge.dischargeEntries.findIndex(
      (entry) => entry.dischargeTypeId === dischargeType
    );

    let newDischargeEntries: DischargeEntry[];

    if (existingIndex >= 0) {
      // Remove if already selected
      newDischargeEntries = formEntry.discharge.dischargeEntries.filter((_, index) => index !== existingIndex);
    } else {
      // Add new discharge entry
      const newEntry: DischargeEntry = {
        dischargeTypeId: dischargeType,
        dischargeTypeName: dischargeType,
        quantity: "",
        unit: "m³",
      };
      newDischargeEntries = [...formEntry.discharge.dischargeEntries, newEntry];
    }

    onFieldChange("dischargeEntries", newDischargeEntries);
    onFormEntryChange({
      ...formEntry,
      discharge: {
        ...formEntry.discharge,
        dischargeEntries: newDischargeEntries,
      },
    });
  };

  const handleDischargeQuantityChange = (index: number, value: string) => {
    const newDischargeEntries = [...formEntry.discharge.dischargeEntries];
    newDischargeEntries[index] = {
      ...newDischargeEntries[index],
      quantity: value === "" ? "" : parseFloat(value),
    };

    onFieldChange(`discharge_quantity_${index}`, value);
    onFormEntryChange({
      ...formEntry,
      discharge: {
        ...formEntry.discharge,
        dischargeEntries: newDischargeEntries,
      },
    });
  };

  const handleAddPollutant = () => {
    const newIndex = formEntry.discharge.pollutants.length;
    onFormEntryChange({
      ...formEntry,
      discharge: {
        ...formEntry.discharge,
        pollutants: [...formEntry.discharge.pollutants, { ...emptyPollutant }],
      },
    });
    // Clear any existing errors for the new pollutant
    onFieldChange(`pollutant_${newIndex}`, "");
    onFieldChange(`concentration_${newIndex}`, "");
    onFieldChange(`monitoring_${newIndex}`, "");
    onFieldChange(`receiverConcentration_${newIndex}`, "");
  };

  const handleRemovePollutant = (index: number) => {
    const updatedPollutants = formEntry.discharge.pollutants.filter((_, i) => i !== index);
    onFormEntryChange({
      ...formEntry,
      discharge: {
        ...formEntry.discharge,
        pollutants: updatedPollutants,
      },
    });

    // Clear errors for the removed pollutant and reindex remaining ones
    onFieldChange(`pollutant_${index}`, "");
    onFieldChange(`concentration_${index}`, "");
    onFieldChange(`monitoring_${index}`, "");
    onFieldChange(`receiverConcentration_${index}`, "");

    // Clear errors for any pollutants that were after the removed one
    for (let i = index + 1; i < formEntry.discharge.pollutants.length; i++) {
      onFieldChange(`pollutant_${i}`, "");
      onFieldChange(`concentration_${i}`, "");
      onFieldChange(`monitoring_${i}`, "");
      onFieldChange(`receiverConcentration_${i}`, "");
    }
  };

  const handlePollutantChange = (index: number, field: keyof PollutantEntry, value: any) => {
    const updatedPollutants = [...formEntry.discharge.pollutants];

    if (field === "pollutantId") {
      const selectedPollutant = pollutants.find((p) => p.id === value);
      if (selectedPollutant) {
        updatedPollutants[index] = {
          ...updatedPollutants[index],
          pollutantId: value,
          pollutantName: selectedPollutant.name,
        };
      }
      // Clear the error for this field when user makes a selection
      onFieldChange(`pollutant_${index}`, value);
    } else if (field === "concentration") {
      updatedPollutants[index] = {
        ...updatedPollutants[index],
        [field]: value === "" ? "" : parseFloat(value),
      };
      // Clear the error for this field when user types
      onFieldChange(`concentration_${index}`, value);
    } else if (field === "concentrationInReceiver") {
      updatedPollutants[index] = {
        ...updatedPollutants[index],
        [field]: value === "" ? "" : parseFloat(value),
      };
      // Clear the error for this field when user types
      onFieldChange(`receiverConcentration_${index}`, value);
    } else {
      updatedPollutants[index] = {
        ...updatedPollutants[index],
        [field]: value,
      };
    }

    onFormEntryChange({
      ...formEntry,
      discharge: {
        ...formEntry.discharge,
        pollutants: updatedPollutants,
      },
    });
  };

  const handlePollutantMonitoringDecision = (index: number, value: string) => {
    const hasMonitoring = value === "yes";
    const updatedPollutants = [...formEntry.discharge.pollutants];
    updatedPollutants[index] = {
      ...updatedPollutants[index],
      hasMonitoring,
      concentrationInReceiver: hasMonitoring ? updatedPollutants[index].concentrationInReceiver : "",
    };

    // Clear the monitoring error when user makes a decision
    onFieldChange(`monitoring_${index}`, hasMonitoring);

    // If monitoring is disabled, clear the receiver concentration error
    if (!hasMonitoring) {
      onFieldChange(`receiverConcentration_${index}`, "");
    }

    onFormEntryChange({
      ...formEntry,
      discharge: {
        ...formEntry.discharge,
        pollutants: updatedPollutants,
      },
    });
  };

  // Get search value for a pollutant
  const getSearchValue = (index: number, pollutant: PollutantEntry) => {
    if (searchValues[index] !== undefined) {
      return searchValues[index];
    }
    return pollutant.pollutantId 
      ? pollutants.find(p => p.id === pollutant.pollutantId)?.name || ""
      : "";
  };

  // Handle search input change
  const handleSearchChange = (index: number, value: string) => {
    setSearchValues(prev => ({ ...prev, [index]: value }));
    setOpenDropdowns(prev => ({ ...prev, [index]: true }));
  };

  // Handle search input click
  const handleSearchClick = (index: number) => {
    setOpenDropdowns(prev => ({ ...prev, [index]: !prev[index] }));
  };

  // Handle pollutant selection
  const handlePollutantSelect = (index: number, pollutant: { id: string; name: string }) => {
    handlePollutantChange(index, "pollutantId", pollutant.id);
    setSearchValues(prev => ({ ...prev, [index]: pollutant.name }));
    setOpenDropdowns(prev => ({ ...prev, [index]: false }));
  };

  // Handle clear selection
  const handleClearSelection = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    handlePollutantChange(index, "pollutantId", "");
    setSearchValues(prev => ({ ...prev, [index]: "" }));
    setOpenDropdowns(prev => ({ ...prev, [index]: false }));
  };

  // Handle clicks outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is specifically on the modal scrollbar
      const modalScrollableArea = document.querySelector('[class*="overflow-y-auto"]');
      if (modalScrollableArea) {
        const modalRect = modalScrollableArea.getBoundingClientRect();
        // Only protect the scrollbar area
        const isOnModalScrollbar = 
          event.clientX > modalRect.right - 20 && 
          event.clientX <= modalRect.right && 
          event.clientY >= modalRect.top && 
          event.clientY <= modalRect.bottom;
        
        if (isOnModalScrollbar) {
          return;
        }
      }

      Object.keys(dropdownRefs.current).forEach(key => {
        const index = parseInt(key);
        const dropdownElement = dropdownRefs.current[index];
        
        if (!dropdownElement) return;
        
        // Check if click is within the dropdown bounds
        const rect = dropdownElement.getBoundingClientRect();
        const isWithinDropdownBounds = event.clientX >= rect.left && event.clientX <= rect.right && 
                                       event.clientY >= rect.top && event.clientY <= rect.bottom;
        
        if (isWithinDropdownBounds) {
          return;
        }
        
        // Only close if click is truly outside and not currently scrolling
        if (!dropdownElement.contains(event.target as Node) && !isScrolling) {
          setOpenDropdowns(prev => ({
            ...prev,
            [index]: false
          }));
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isScrolling]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenDropdowns({});
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  return (
    <div className="space-y-6">
      {/* Discharge Configuration */}
      <div className="space-y-6">
        {/* Discharge Types Section */}
        <div className="space-y-3 pt-2 mt-6">
          <Label className={`text-base ${errors.dischargeEntries ? "text-red-500" : "text-gray-700"}`}>
            {t("water.dischargeTypes")} *
          </Label>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {dischargeTypes.length === 0 ? (
              <div className="text-gray-500 text-base italic text-center py-6">
                {t("water.noDischargeTypesAvailable")}
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-2 p-4 bg-gray-50 border-b border-gray-200">
                  {dischargeTypes.map((type) => {
                    const isSelected = formEntry.discharge.dischargeEntries.some(
                      (entry) => entry.dischargeTypeId === type
                    );
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleDischargeTypeToggle(type)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 ${
                          isSelected
                            ? "bg-teal-600 text-white hover:bg-teal-700"
                            : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                        }`}
                      >
                        <span className="truncate">{type}</span>
                      </button>
                    );
                  })}
                </div>
                {formEntry.discharge.dischargeEntries.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {formEntry.discharge.dischargeEntries.map((discharge, index) => {
                      const quantityError = errors[`discharge_quantity_${index}`];
                      return (
                        <div key={index} className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center min-w-0 flex-1 mr-3">
                              <div className="h-2 w-2 bg-teal-600 rounded-full mr-2 flex-shrink-0" />
                              <span className="text-sm font-medium text-gray-800 truncate">
                                {discharge.dischargeTypeName}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 w-full max-w-[280px] flex-shrink-0">
                              <div className="relative w-full">
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={discharge.quantity === "" ? "" : discharge.quantity}
                                  onChange={(e) => handleDischargeQuantityChange(index, e.target.value)}
                                  onBlur={() => onFieldBlur(`discharge_quantity_${index}`)}
                                  placeholder="0.00"
                                  autoComplete="off"
                                  className={`pr-12 h-11 w-full rounded-md text-base ${
                                    touched[`discharge_quantity_${index}`] && quantityError
                                      ? "border-red-500 focus:ring-red-500"
                                      : "border-gray-300 focus:ring-teal-500 focus:border-teal-500"
                                  }`}
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                  <span className="text-gray-500 text-sm">{discharge.unit}</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleDischargeTypeToggle(discharge.dischargeTypeId)}
                                className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-full hover:bg-red-50 flex-shrink-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          {quantityError && (
                            <div className="text-red-500 text-sm flex items-center gap-1 mt-1.5 ml-4">
                              <Info className="h-3 w-3" />
                              {quantityError}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="py-6 px-4 text-center text-gray-500 text-sm bg-gray-25">
                    <div className="flex items-center justify-center gap-1.5">
                      <Info className="h-4 w-4 text-gray-400" />
                      <span>{t("water.selectDischargeTypesMessage")}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {errors.dischargeEntries && (
            <div className="text-red-500 text-sm flex items-center gap-1 mt-1">
              <Info className="h-3 w-3" />
              {errors.dischargeEntries}
            </div>
          )}
        </div>

        {/* Pollutants Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Label className={`text-base ${errors.pollutants ? "text-red-500" : "text-gray-700"}`}>
                {t("water.pollutantsMonitoring")} *
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>{t("water.pollutantRequired")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Button
              type="button"
              onClick={handleAddPollutant}
              size="sm"
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Plus className="h-4 w-4 mr-1" />
              {t("water.addPollutantButton")}
            </Button>
          </div>

          {errors.pollutants && (
            <div className="text-red-500 text-sm flex items-center gap-1 mt-1">
              <Info className="h-3 w-3" />
              {errors.pollutants}
            </div>
          )}

          {formEntry.discharge.pollutants.length === 0 ? (
            <div className="py-6 px-4 text-center text-gray-500 text-sm bg-gray-25 rounded-lg border border-gray-200">
              <div className="flex items-center justify-center gap-1.5">
                <Info className="h-4 w-4 text-gray-400" />
                <span>{t("water.addPollutantsMessage")}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {formEntry.discharge.pollutants.map((pollutant, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* First Row: Pollutant Type, Concentration, Remove Button */}
                  <div className="grid grid-cols-12 gap-4 items-start mb-3">
                    {/* Pollutant Type */}
                    <div className="col-span-7">
                      <div className="flex items-center gap-2 mb-1">
                        <Label className="text-sm text-gray-700">{t("water.pollutantLabel")} *</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3 w-3 text-gray-400 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>{t("water.pollutantTooltip")}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div className="relative" ref={(el) => { dropdownRefs.current[index] = el; }}>
                        <div className="relative">
                          <input
                            type="text"
                            value={getSearchValue(index, pollutant)}
                            onChange={(e) => handleSearchChange(index, e.target.value)}
                            onClick={() => handleSearchClick(index)}
                            placeholder={t("water.selectPlaceholder")}
                            autoComplete="off"
                            spellCheck="false"
                            className={`w-full h-10 px-3 py-2 cursor-pointer pr-10 border border-gray-200 rounded-md outline-none focus:outline-none ${
                              touched[`pollutant_${index}`] && errors[`pollutant_${index}`]
                                ? "border-red-500"
                                : ""
                            }`}
                          />
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                            {getSearchValue(index, pollutant) && (
                              <button
                                type="button"
                                onClick={(e) => handleClearSelection(index, e)}
                                className="text-gray-400 hover:text-gray-600 p-1 rounded-full mr-1"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            )}
                            <ChevronDown
                              className={`h-4 w-4 text-gray-500 transition-transform ${openDropdowns[index] ? "transform rotate-180" : ""}`}
                            />
                          </div>
                        </div>
                        
                        {openDropdowns[index] && (
                          <div
                            className="absolute z-50 w-full mt-1 border bg-white rounded-md shadow-lg overflow-hidden"
                            style={{ maxHeight: "320px", overflowY: "auto" }}
                            onMouseDown={(e) => {
                              // Prevent dropdown from closing when clicking on scrollbar
                              const target = e.currentTarget;
                              const rect = target.getBoundingClientRect();
                              const isScrollbarClick = e.clientX > rect.right - 16;
                              
                              if (isScrollbarClick) {
                                e.stopPropagation();
                                e.preventDefault();
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onPointerDown={(e) => {
                              e.stopPropagation();
                            }}
                            onScroll={() => {
                              setIsScrolling(true);
                              setTimeout(() => setIsScrolling(false), 150);
                            }}
                          >
                            {pollutants
                              .filter((p) => {
                                const searchValue = searchValues[index] || "";
                                let matchesSearch = true;
                                if (searchValue) {
                                  const normalizedPollutantName = normalizeText(p.name);
                                  const normalizedSearchValue = normalizeText(searchValue);
                                  matchesSearch = normalizedPollutantName.includes(normalizedSearchValue);
                                }
                                
                                // Show the currently selected pollutant for this index
                                if (p.id === pollutant.pollutantId) return matchesSearch;
                                
                                // Filter out pollutants already selected in discharge
                                const isSelectedInDischarge = formEntry.discharge.pollutants.some(
                                  (selectedPollutant, selectedIndex) =>
                                    selectedIndex !== index && selectedPollutant.pollutantId === p.id
                                );
                                return !isSelectedInDischarge && matchesSearch;
                              })
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((p) => (
                                <button
                                  key={p.id}
                                  type="button"
                                  className="w-full text-left px-4 py-3.5 hover:bg-teal-50 cursor-pointer transition-colors border-b border-gray-100 last:border-0"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handlePollutantSelect(index, { id: p.id, name: p.name });
                                  }}
                                >
                                  {p.name}
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                      {errors[`pollutant_${index}`] && (
                        <p className="text-xs text-red-500 mt-1">{errors[`pollutant_${index}`]}</p>
                      )}
                    </div>

                    {/* Concentration */}
                    <div className="col-span-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Label className="text-sm text-gray-700">{t("water.concentrationLabel")} *</Label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3 w-3 text-gray-400 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>{t("water.concentrationTooltip")}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div className="relative">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={pollutant.concentration === "" ? "" : pollutant.concentration}
                          onChange={(e) => handlePollutantChange(index, "concentration", e.target.value)}
                          onBlur={() => onFieldBlur(`concentration_${index}`)}
                          placeholder="0.00"
                          className={`h-10 text-base pr-12 border-gray-200 hover:border-gray-300 focus:border-teal-500 focus:ring-teal-500 ${
                            touched[`concentration_${index}`] && errors[`concentration_${index}`]
                              ? "border-red-400 focus:border-red-500 focus:ring-red-500"
                              : ""
                          }`}
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">mg/L</span>
                      </div>
                      {errors[`concentration_${index}`] && (
                        <p className="text-xs text-red-500 mt-1">{errors[`concentration_${index}`]}</p>
                      )}
                    </div>

                    {/* Remove Button */}
                    <div className="col-span-1 flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleRemovePollutant(index)}
                        className="h-10 w-10 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 mt-4"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Second Row: Monitoring Question and Conditional Field */}
                  <div className="border-t border-gray-100 pt-3">
                    <div className="grid grid-cols-12 gap-4 items-start">
                      {/* Monitoring Toggle */}
                      <div className="col-span-7">
                        <div className="flex items-start gap-2 mb-1">
                          <div className="flex-1 min-w-0">
                            <Label className="text-sm text-gray-700">{t("water.monitoringLabel")} *</Label>
                          </div>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3 w-3 text-gray-400 cursor-help mt-1" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p>{t("water.monitoringTooltip")}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div className="flex gap-1 mt-2">
                          <button
                            type="button"
                            onClick={() => handlePollutantMonitoringDecision(index, "no")}
                            className={`w-16 h-10 text-sm rounded-lg border transition-all duration-200 ${
                              pollutant.hasMonitoring === false
                                ? "bg-red-600 text-white border-red-600 shadow-sm"
                                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                            }`}
                          >
                            {t("water.no")}
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePollutantMonitoringDecision(index, "yes")}
                            className={`w-16 h-10 text-sm rounded-lg border transition-all duration-200 ${
                              pollutant.hasMonitoring === true
                                ? "bg-teal-600 text-white border-teal-600 shadow-sm"
                                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                            }`}
                          >
                            {t("water.yes")}
                          </button>
                        </div>
                        {errors[`monitoring_${index}`] && (
                          <p className="text-xs text-red-500 mt-1">{errors[`monitoring_${index}`]}</p>
                        )}
                      </div>

                      {/* Receiver Concentration (when monitoring is enabled) */}
                      {pollutant.hasMonitoring === true && (
                        <div className="col-span-5">
                          <div className="flex items-center gap-2 mb-1">
                            <Label className="text-sm text-gray-700">{t("water.receiverConcentrationLabel")} *</Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-3 w-3 text-gray-400 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p>{t("water.receiverConcentrationTooltip")}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <div className="relative">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={pollutant.concentrationInReceiver === "" ? "" : pollutant.concentrationInReceiver}
                              onChange={(e) => handlePollutantChange(index, "concentrationInReceiver", e.target.value)}
                              onBlur={() => onFieldBlur(`receiverConcentration_${index}`)}
                              placeholder="0.00"
                              className={`h-10 text-base pr-12 bg-gray-50 border-gray-200 hover:border-gray-300 focus:border-teal-500 focus:ring-teal-500 ${
                                touched[`receiverConcentration_${index}`] && errors[`receiverConcentration_${index}`]
                                  ? "border-red-400 focus:border-red-500 focus:ring-red-500"
                                  : ""
                              }`}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                              mg/L
                            </span>
                          </div>
                          {errors[`receiverConcentration_${index}`] && (
                            <p className="text-xs text-red-500 mt-1">{errors[`receiverConcentration_${index}`]}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
